import { createSignal } from "solid-js";
import { specifications } from "@tailor-frame/patterns";

/** One garment of a print set, with the letter its pieces are prefixed by (F‑A, T‑B…) so garments never mix. */
export interface PrintSetMember { patternId: string; code: string; name: string }

/** Patterns styled, printed and laid out together, drafted from the same body. */
export interface PrintSet { id: string; name: string; description: string; members: PrintSetMember[] }

/** Ready-made groups: one click fills the group with them. */
export const printSets: PrintSet[] = [
  {
    id: "dracula-outfit",
    name: "Dracula outfit",
    description: "Frock coat, trousers, waistcoat and cape.",
    members: [
      { patternId: "supreme-frock-coat", code: "F", name: "Frock coat" },
      { patternId: "classic-trousers", code: "T", name: "Trousers" },
      { patternId: "supreme-vest", code: "W", name: "Waistcoat" },
      { patternId: "supreme-regular-cape", code: "C", name: "Cape" },
    ],
  },
  {
    id: "three-piece-suit",
    name: "Three-piece suit",
    description: "Frock coat, trousers and waistcoat.",
    members: [
      { patternId: "supreme-frock-coat", code: "F", name: "Frock coat" },
      { patternId: "classic-trousers", code: "T", name: "Trousers" },
      { patternId: "supreme-vest", code: "W", name: "Waistcoat" },
    ],
  },
];

/** The id of the group chosen in the library. */
export const GROUP_ID = "group";

// The patterns ticked in the library, in the order they were added, kept between visits.
const groupStorageKey = "tailor-frame:group";
function loadGroup(): string[] {
  try {
    const saved = JSON.parse(localStorage.getItem(groupStorageKey) ?? "[]") as unknown;
    return Array.isArray(saved) ? saved.filter((id): id is string => typeof id === "string" && specifications.some((entry) => entry.specification.pattern.id === id)) : [];
  } catch { return []; }
}
const [group, setGroupSignal] = createSignal<string[]>(loadGroup());
export { group };

export function setGroup(ids: string[]) {
  const next = [...new Set(ids)];
  setGroupSignal(next);
  try { localStorage.setItem(groupStorageKey, JSON.stringify(next)); } catch { /* storage unavailable: keep in memory only */ }
}
export const toggleInGroup = (id: string) => setGroup(group().includes(id) ? group().filter((item) => item !== id) : [...group(), id]);

/**
 * A garment's letter: the first letter of a word of its name not already
 * taken, the last word first (Frock coat → C, then F), then any free letter.
 */
function codeFor(name: string, taken: Set<string>): string {
  const words = name.toUpperCase().split(/[^A-Z]+/).filter(Boolean).reverse();
  const letters = [...words.map((word) => word[0]), ..."ABCDEFGHIJKLMNOPQRSTUVWXYZ"];
  return letters.find((letter) => !taken.has(letter)) ?? String(taken.size + 1);
}

/** The group as a print set: named and lettered as its preset when it is one, otherwise lettered from the pattern names. */
export function groupSet(ids: string[]): PrintSet {
  const preset = printSets.find((set) => set.members.length === ids.length && set.members.every((member, index) => member.patternId === ids[index]));
  if (preset) return { ...preset, id: GROUP_ID };
  const taken = new Set<string>();
  const members = ids.flatMap((patternId): PrintSetMember[] => {
    const entry = specifications.find((item) => item.specification.pattern.id === patternId);
    if (!entry) return [];
    const name = entry.specification.pattern.name;
    const code = codeFor(name, taken);
    taken.add(code);
    return [{ patternId, code, name }];
  });
  return { id: GROUP_ID, name: members.length === 1 ? members[0].name : `Group of ${members.length}`, description: members.map((member) => member.name).join(", ") + ".", members };
}

/** A preset by id, or the library's group (reactive: it follows the group as it changes). */
export const printSetById = (id: string) => id === GROUP_ID ? (group().length > 0 ? groupSet(group()) : undefined) : printSets.find((set) => set.id === id);
